import { ExpirationOptions, type ExpirationType } from "@repo/api/src/schemas/order.schema";
import {
	ArrowRight,
	Box,
	Camera,
	Check,
	Copy,
	ExternalLink,
	Layers3,
	MoreHorizontal,
	Pencil,
	Save,
	Trash2,
	Zap,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { trpc } from "../../../lib/trpc";
import { getAccountCopy } from "../../data/accountCopy";
import { useTranslation } from "../../i18n";
import { encodeDescriptionWithMetadata, parseDescriptionWithMetadata } from "../../utils/splatMetadata";
import {
	Alert,
	Button,
	ButtonLink,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
	Empty,
	Field,
	FieldDescription,
	FieldLabel,
	Input,
	LoadingState,
	Modal,
	Select,
	Textarea,
} from "../ui";
import { LodDeliveryDialog } from "./LodDeliveryDialog";
import { SplatEditorDialog } from "./SplatEditorDialog";
import { ViewpointDialog } from "./ViewpointDialog";
import { formatBytes, formatDate, formatDateTime } from "./format";
import type { UploadRow } from "./types";

interface UploadLibraryProps {
	uploads: UploadRow[];
	loading?: boolean;
}
type EditableVisibility = "public" | "unlisted" | "private";
const normalizeVisibility = (value: UploadRow["visibility"]): EditableVisibility =>
	value === "unlisted" || value === "private" ? value : "public";

export function UploadLibrary({ uploads, loading = false }: UploadLibraryProps) {
	const { locale, localizedPath } = useTranslation();
	const copy = getAccountCopy(locale).library;
	const utils = trpc.useUtils();
	const [editing, setEditing] = useState<UploadRow | null>(null);
	const [splatEditing, setSplatEditing] = useState(false);
	const [lodEditing, setLodEditing] = useState(false);
	const [lodAutoStart, setLodAutoStart] = useState(false);
	const [lodFromList, setLodFromList] = useState(false);
	const [viewpointEditing, setViewpointEditing] = useState(false);
	const [editTitle, setEditTitle] = useState("");
	const [editDescription, setEditDescription] = useState("");
	const [editVisibility, setEditVisibility] = useState<EditableVisibility>("public");
	const [editExpirationExtension, setEditExpirationExtension] = useState<ExpirationType | "">("");
	const [deleting, setDeleting] = useState<UploadRow | null>(null);
	const [discardOpen, setDiscardOpen] = useState(false);
	const [notice, setNotice] = useState<{ tone: "success" | "error"; text: string } | null>(null);
	const [copiedId, setCopiedId] = useState<string | null>(null);
	const copyTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const updateUpload = trpc.library.updateUpload.useMutation({ networkMode: "always" });
	const deleteUpload = trpc.library.deleteUpload.useMutation({ networkMode: "always" });
	const existingDescription = parseDescriptionWithMetadata(editing?.description);
	const metadataLength = encodeDescriptionWithMetadata("", existingDescription.metadata).length;
	const streamedCount = uploads.filter(
		upload => upload.modelType === "lod" || parseDescriptionWithMetadata(upload.description).metadata?.lod,
	).length;
	const dirty =
		!!editing &&
		(editTitle !== (editing.title ?? "") ||
			editDescription !== existingDescription.displayDescription ||
			editVisibility !== normalizeVisibility(editing.visibility) ||
			editExpirationExtension !== "");
	useEffect(() => () => clearTimeout(copyTimer.current), []);
	useEffect(() => {
		if (!dirty) return;
		const preventLoss = (event: BeforeUnloadEvent) => {
			event.preventDefault();
			event.returnValue = "";
		};
		window.addEventListener("beforeunload", preventLoss);
		return () => window.removeEventListener("beforeunload", preventLoss);
	}, [dirty]);
	const startEditing = (upload: UploadRow) => {
		updateUpload.reset();
		setEditing(upload);
		setEditTitle(upload.title ?? "");
		setEditDescription(parseDescriptionWithMetadata(upload.description).displayDescription);
		setEditVisibility(normalizeVisibility(upload.visibility));
		setEditExpirationExtension("");
	};
	const closeEditor = () => {
		if (updateUpload.isLoading) return;
		if (dirty) setDiscardOpen(true);
		else setEditing(null);
	};
	const saveEditing = async () => {
		if (!editing || updateUpload.isLoading) return;
		try {
			await updateUpload.mutateAsync({
				id: editing.id,
				title: editTitle.trim() || null,
				description: encodeDescriptionWithMetadata(editDescription.trim(), existingDescription.metadata).trim() || null,
				visibility: editVisibility,
				...(editExpirationExtension ? { expirationType: editExpirationExtension } : {}),
			});
			setEditing(null);
			setNotice({ tone: "success", text: copy.saved });
			await utils.library.listMine.invalidate();
		} catch {
			/* Mutation error stays beside the retained fields. */
		}
	};
	const copyLink = async (shareId: string) => {
		try {
			await navigator.clipboard.writeText(`${window.location.origin}/viewer/${shareId}`);
			clearTimeout(copyTimer.current);
			setCopiedId(shareId);
			setNotice({ tone: "success", text: copy.copied });
			copyTimer.current = setTimeout(() => setCopiedId(null), 2000);
		} catch {
			setNotice({ tone: "error", text: copy.copyFailed });
		}
	};
	const removeUpload = async () => {
		if (!deleting || deleteUpload.isLoading) return;
		try {
			await deleteUpload.mutateAsync({ id: deleting.id });
			setDeleting(null);
			setNotice({ tone: "success", text: copy.deleted });
			await Promise.all([utils.library.listMine.invalidate(), utils.library.summary.invalidate()]);
		} catch {
			/* Keep the confirmation open so the user can retry or cancel. */
		}
	};
	return (
		<>
			{notice && (
				<div className="mb-4">
					<Alert tone={notice.tone} aria-live="polite">
						{notice.text}
					</Alert>
				</div>
			)}
			<section className="account-library" aria-busy={loading} aria-labelledby="library-heading">
				<header className="account-library-toolbar">
					<div>
						<Layers3 size={16} aria-hidden="true" />
						<h2 id="library-heading">{copy.title}</h2>
						<span className="account-count">{loading ? "—" : uploads.length}</span>
					</div>
					{!loading && uploads.length > 0 && (
						<p>{locale === "ja" ? `${streamedCount}件がLOD配信中` : `${streamedCount} streaming with LOD`}</p>
					)}
				</header>
				{loading ? (
					<LoadingState label={copy.loading} />
				) : !uploads.length ? (
					<Empty
						title={copy.emptyTitle}
						description={copy.emptyBody}
						action={<ButtonLink href={`${localizedPath("/")}#upload`}>{copy.upload}</ButtonLink>}
					/>
				) : (
					<>
						<div className="account-list-columns" aria-hidden="true">
							<span>{locale === "ja" ? "モデル" : "Model"}</span>
							<span>{locale === "ja" ? "配信" : "Delivery"}</span>
							<span>{copy.visibility}</span>
							<span>{locale === "ja" ? "操作" : "Actions"}</span>
						</div>
						<ul className="account-model-list">
							{uploads.map(upload => {
								const parsed = parseDescriptionWithMetadata(upload.description);
								const streamed = upload.modelType === "lod" || !!parsed.metadata?.lod;
								const editable = ["ply", "spz", "sog", "splat"].includes(upload.modelType ?? "");
								return (
									<li key={upload.id} className="account-model-row">
										<div className="account-model-identity">
											<div className="account-file-mark">
												<Box size={23} strokeWidth={1.2} aria-hidden="true" />
												<span>{upload.modelType?.toUpperCase() || "3D"}</span>
											</div>
											<div className="account-model-copy">
												<a href={`/viewer/${upload.shareId}`} className="account-model-title">
													{upload.title || copy.untitled}
												</a>
												{parsed.displayDescription && (
													<p className="account-model-description">{parsed.displayDescription}</p>
												)}
												<div className="account-model-meta">
													<span>{formatBytes(upload.fileSizeBytes)}</span>
													<span>{formatDate(upload.createdAt, locale)}</span>
													<span>
														{upload.viewCount} {copy.views}
													</span>
												</div>
											</div>
										</div>
										<div className="account-model-delivery">
											<span className="account-delivery-badge" data-streamed={streamed}>
												<span aria-hidden="true" />
												{streamed
													? locale === "ja"
														? "LOD配信"
														: "LOD streaming"
													: locale === "ja"
														? "元データ"
														: "Original"}
											</span>
											{editable && (
												<Button
													variant="ghost"
													className="account-optimize-button"
													onClick={() => {
														startEditing(upload);
														setLodAutoStart(!streamed);
														setLodFromList(true);
														setLodEditing(true);
													}}
												>
													<Zap size={13} />
													{streamed
														? locale === "ja"
															? "LODを設定"
															: "LOD settings"
														: locale === "ja"
															? "LOD配信にする"
															: "Optimize delivery"}
													<ArrowRight size={13} />
												</Button>
											)}
										</div>
										<div className="account-model-access">
											<span>{copy.visibilityLabels[normalizeVisibility(upload.visibility)]}</span>
											{upload.expiresAt && (
												<small>
													{new Date(upload.expiresAt).getFullYear() >= 2074
														? copy.expirationUnlimited
														: `${new Date(upload.expiresAt).getTime() <= Date.now() ? copy.expired : copy.expires}: ${formatDateTime(upload.expiresAt, locale)}`}
												</small>
											)}
										</div>
										<div className="account-model-actions">
											<ButtonLink
												href={`/viewer/${upload.shareId}`}
												variant="ghost"
												target="_blank"
												rel="noopener noreferrer"
											>
												<ExternalLink size={16} />
												{copy.open}
											</ButtonLink>
											<Button
												variant="ghost"
												size="icon"
												aria-label={copiedId === upload.shareId ? copy.copied : copy.copy}
												title={copiedId === upload.shareId ? copy.copied : copy.copy}
												onClick={() => void copyLink(upload.shareId)}
											>
												{copiedId === upload.shareId ? <Check size={16} /> : <Copy size={16} />}
											</Button>
											<DropdownMenu>
												<DropdownMenuTrigger asChild>
													<Button
														variant="ghost"
														size="icon"
														aria-label={`${copy.more}: ${upload.title || copy.untitled}`}
													>
														<MoreHorizontal size={18} />
													</Button>
												</DropdownMenuTrigger>
												<DropdownMenuContent align="end" className="account-surface account-menu">
													<DropdownMenuItem onSelect={() => startEditing(upload)}>
														<Pencil size={16} />
														{copy.edit}
													</DropdownMenuItem>
													{["ply", "spz", "sog", "splat"].includes(upload.modelType ?? "") && (
														<>
															<DropdownMenuItem
																onSelect={() => {
																	startEditing(upload);
																	setLodAutoStart(true);
																	setLodFromList(true);
																	setLodEditing(true);
																}}
															>
																<Zap size={16} />
																{locale === "ja" ? "自動でLODを生成する" : "Generate LOD automatically"}
															</DropdownMenuItem>
															<DropdownMenuItem
																onSelect={() => {
																	startEditing(upload);
																	setLodAutoStart(false);
																	setLodFromList(true);
																	setLodEditing(true);
																}}
															>
																<Zap size={16} />
																{locale === "ja" ? "LOD配信を設定" : "Set up LOD delivery"}
															</DropdownMenuItem>
															<DropdownMenuItem
																onSelect={() => {
																	startEditing(upload);
																	setViewpointEditing(true);
																}}
															>
																<Camera size={16} />
																{locale === "ja" ? "最初の視点を設定" : "Set starting viewpoint"}
															</DropdownMenuItem>
														</>
													)}
													<DropdownMenuSeparator />
													<DropdownMenuItem
														variant="destructive"
														onSelect={() => {
															deleteUpload.reset();
															setDeleting(upload);
														}}
													>
														<Trash2 size={16} />
														{copy.delete}
													</DropdownMenuItem>
												</DropdownMenuContent>
											</DropdownMenu>
										</div>
									</li>
								);
							})}
						</ul>
					</>
				)}
			</section>
			<Modal
				className="account-surface account-dialog account-edit-dialog"
				open={!!editing && !splatEditing && !lodEditing && !viewpointEditing}
				onOpenChange={open => {
					if (!open) closeEditor();
				}}
				title={copy.edit}
				description={editing?.title || copy.untitled}
				closeLabel={copy.cancel}
				busy={updateUpload.isLoading}
				footer={
					<>
						<Button variant="secondary" onClick={closeEditor} disabled={updateUpload.isLoading}>
							{copy.cancel}
						</Button>
						<Button type="submit" form="edit-share" disabled={!dirty || updateUpload.isLoading}>
							<Save size={16} />
							{updateUpload.isLoading ? copy.saving : copy.save}
						</Button>
					</>
				}
			>
				<form
					id="edit-share"
					onSubmit={event => {
						event.preventDefault();
						void saveEditing();
					}}
					className="account-edit-form"
					aria-busy={updateUpload.isLoading}
				>
					<fieldset disabled={updateUpload.isLoading} className="account-edit-layout">
						<div className="account-edit-fields">
							<h3 className="account-section-label">{locale === "ja" ? "公開情報" : "Share details"}</h3>
							<Field>
								<FieldLabel htmlFor="share-title">{copy.fieldTitle}</FieldLabel>
								<Input
									id="share-title"
									value={editTitle}
									onChange={event => setEditTitle(event.target.value)}
									maxLength={200}
									placeholder={copy.fieldTitlePlaceholder}
								/>
							</Field>
							<Field>
								<FieldLabel htmlFor="share-description">{copy.fieldDescription}</FieldLabel>
								<Textarea
									id="share-description"
									value={editDescription}
									onChange={event => setEditDescription(event.target.value)}
									maxLength={Math.max(0, 3000 - metadataLength)}
									rows={3}
									placeholder={copy.fieldDescriptionPlaceholder}
								/>
							</Field>
							<div className="account-public-fields">
								<Field>
									<FieldLabel htmlFor="share-visibility">{copy.visibility}</FieldLabel>
									<Select
										id="share-visibility"
										value={editVisibility}
										onChange={event => setEditVisibility(event.target.value as EditableVisibility)}
										aria-describedby="visibility-help"
									>
										<option value="public">{copy.visibilityLabels.public}</option>
										<option value="unlisted">{copy.visibilityLabels.unlisted}</option>
										<option value="private">{copy.visibilityLabels.private}</option>
									</Select>
									<FieldDescription id="visibility-help">{copy.visibilityHint}</FieldDescription>
								</Field>
								<Field>
									<FieldLabel htmlFor="share-expiration">{copy.expiration}</FieldLabel>
									<Select
										id="share-expiration"
										value={editExpirationExtension}
										onChange={event => setEditExpirationExtension(event.target.value as ExpirationType | "")}
										aria-describedby="expiration-help"
									>
										<option value="">{copy.expirationKeep}</option>
										<option value={ExpirationOptions.HOUR}>{copy.extendHour}</option>
										<option value={ExpirationOptions.DAY}>{copy.extendDay}</option>
										<option value={ExpirationOptions.WEEK}>{copy.extendWeek}</option>
										<option value={ExpirationOptions.UNLIMITED}>{copy.makeUnlimited}</option>
									</Select>
									<FieldDescription id="expiration-help">
										{editing?.expiresAt && (
											<>
												{copy.currentExpiration}:{" "}
												{new Date(editing.expiresAt).getFullYear() >= 2074
													? copy.expirationUnlimited
													: formatDateTime(editing.expiresAt, locale)}
												.{" "}
											</>
										)}
										{copy.expirationHint}
									</FieldDescription>
								</Field>
							</div>
						</div>
						{editing && ["ply", "spz", "sog", "splat"].includes(editing.modelType ?? "") && (
							<section className="account-edit-tools" aria-labelledby="model-tools-heading">
								<h3 id="model-tools-heading" className="account-section-label">
									{locale === "ja" ? "3Dと配信" : "Model and delivery"}
								</h3>
								<div className="account-edit-tool">
									<Pencil size={18} aria-hidden="true" />
									<Button type="button" variant="ghost" onClick={() => setSplatEditing(true)}>
										{locale === "ja" ? "SuperSplatで3Dモデルを編集" : "Edit 3D in SuperSplat"}
										<ArrowRight size={14} />
									</Button>
									<p>
										{locale === "ja"
											? "向きや位置を調整し、不要な点を削除できます。"
											: "Adjust orientation, position and unwanted splats."}
									</p>
								</div>
								<div className="account-edit-tool">
									<Zap size={18} aria-hidden="true" />
									<Button
										type="button"
										variant="ghost"
										onClick={() => {
											setLodAutoStart(false);
											setLodFromList(false);
											setLodEditing(true);
										}}
									>
										{locale === "ja" ? "LOD配信を設定" : "Set up streamed delivery (LOD)"}
										<ArrowRight size={14} />
									</Button>
									<p>
										{locale === "ja"
											? "視点に応じて必要なデータを読み込みます。"
											: "Stream detail as the viewer moves."}
									</p>
								</div>
								<div className="account-edit-tool">
									<Camera size={18} aria-hidden="true" />
									<Button type="button" variant="ghost" onClick={() => setViewpointEditing(true)}>
										{locale === "ja" ? "最初の視点を設定" : "Set starting viewpoint"}
										<ArrowRight size={14} />
									</Button>
									<p>
										{locale === "ja"
											? "リンクを開いたときのカメラの位置と向きを設定します。"
											: "Choose the first position and direction."}
									</p>
								</div>
							</section>
						)}
					</fieldset>
					{dirty && (
						<output className="block text-sm text-muted-foreground">
							{updateUpload.isLoading ? copy.saving : copy.unsaved}
						</output>
					)}
					{updateUpload.error && <Alert tone="error">{copy.saveFailed}</Alert>}
				</form>
			</Modal>
			<Modal
				open={discardOpen}
				className="account-surface account-dialog"
				onOpenChange={setDiscardOpen}
				title={copy.discardTitle}
				description={copy.discardBody}
				closeLabel={copy.keepEditing}
				footer={
					<>
						<Button variant="secondary" onClick={() => setDiscardOpen(false)}>
							{copy.keepEditing}
						</Button>
						<Button
							variant="destructive"
							onClick={() => {
								setDiscardOpen(false);
								setEditing(null);
							}}
						>
							{copy.discard}
						</Button>
					</>
				}
			>
				<p className="text-sm text-muted-foreground">{editing?.title || copy.untitled}</p>
			</Modal>
			{editing && splatEditing && (
				<SplatEditorDialog
					upload={editing}
					onClose={() => setSplatEditing(false)}
					onSaved={updated => {
						setEditing(updated);
						setSplatEditing(false);
						setNotice({
							tone: "success",
							text:
								locale === "ja"
									? "3Dモデルを更新しました。共有URLは同じです。"
									: "3D model updated. The sharing URL stays the same.",
						});
					}}
				/>
			)}
			{editing && lodEditing && (
				<LodDeliveryDialog
					upload={editing}
					autoStart={lodAutoStart}
					onClose={() => {
						setLodEditing(false);
						if (lodFromList) setEditing(null);
					}}
					onSaved={updated => {
						setEditing(lodFromList ? null : updated);
						setLodEditing(false);
						setNotice({
							tone: "success",
							text:
								locale === "ja"
									? "配信設定を更新しました。共有URLは同じです。"
									: "Delivery updated. The share URL stays the same.",
						});
					}}
				/>
			)}
			{editing && viewpointEditing && (
				<ViewpointDialog
					upload={editing}
					onClose={() => setViewpointEditing(false)}
					onSaved={updated => {
						setEditing(updated);
						setViewpointEditing(false);
						setNotice({
							tone: "success",
							text:
								locale === "ja"
									? "開始視点を保存しました。共有URLは同じです。"
									: "Starting viewpoint saved. The share URL stays the same.",
						});
					}}
				/>
			)}
			<Modal
				open={!!deleting}
				className="account-surface account-dialog"
				onOpenChange={open => {
					if (!open && !deleteUpload.isLoading) setDeleting(null);
				}}
				title={copy.confirmDelete(deleting?.title || copy.untitled)}
				description={copy.deleteBody}
				closeLabel={copy.cancel}
				busy={deleteUpload.isLoading}
				footer={
					<>
						<Button variant="secondary" onClick={() => setDeleting(null)} disabled={deleteUpload.isLoading}>
							{copy.cancel}
						</Button>
						<Button variant="destructive" onClick={() => void removeUpload()} disabled={deleteUpload.isLoading}>
							<Trash2 size={16} />
							{deleteUpload.isLoading ? copy.deleting : copy.delete}
						</Button>
					</>
				}
			>
				{deleteUpload.error && <Alert tone="error">{copy.deleteFailed}</Alert>}
			</Modal>
		</>
	);
}
